import { useSyncExternalStore } from "react";
import { KS, setAccountUnauthorizedHandler } from "./api.jsx";
import { takeCapturedMagicLinkToken } from "./authLanding.js";

export const SIGNED_IN_HINT_KEY = "karatscan:signed-in";
export const LOGOUT_PENDING_KEY = "karatscan:logout-pending:v1";
export const CLOSURE_UNCONFIRMED_KEY = "karatscan:closure-unconfirmed:v1";
const STORAGE_PROBE_KEY = "karatscan:storage-probe:v1";
const STORAGE_REQUIRED_MESSAGE =
  "Browser storage is required to sign in. Enable it and retry, or request a new link.";
const CLOSURE_UNCONFIRMED_MESSAGE =
  "We could not confirm that your account was closed. Contact support or sign in again to check whether the account is still active.";
const CLOSURE_ACTIVE_MESSAGE =
  "Your account is active, so the earlier close request did not complete. Contact support if you still need help.";
const accountMutationConflict = (message) => Object.assign(new Error(message), {
  name: "AccountMutationConflictError",
  code: "ACCOUNT_MUTATION_IN_PROGRESS",
});

const storageFromBrowser = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const safeRead = (storage, key) => {
  try {
    return { value: storage?.getItem(key) ?? null, error: null };
  } catch (error) {
    return { value: null, error };
  }
};

const safeWrite = (storage, key, value) => {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const safeRemove = (storage, key) => {
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = (storage) => {
  const probe = `probe-${Date.now()}`;
  try {
    storage.setItem(STORAGE_PROBE_KEY, probe);
    const matched = storage.getItem(STORAGE_PROBE_KEY) === probe;
    storage.removeItem(STORAGE_PROBE_KEY);
    return matched;
  } catch {
    return false;
  }
};

const publicUser = (value) => value && typeof value === "object" ? value : null;

export function createSessionStore({
  api = KS,
  storage = storageFromBrowser(),
  windowLike = typeof window !== "undefined" ? window : null,
  token = null,
} = {}) {
  const listeners = new Set();
  const privateOwners = new Map();
  let generation = 0;
  let profileFlight = null;
  let profileController = null;
  let consumeFlight = null;
  let logoutFlight = null;
  let profileUpdateFlight = null;
  let closureFlight = null;
  let requestFlight = null;
  let requestController = null;
  let requestGeneration = 0;
  let tokenInPageMemory = token;
  let onlineRetryArmed = false;
  let started = false;
  let destroyed = false;

  const hint = safeRead(storage, SIGNED_IN_HINT_KEY);
  const pending = safeRead(storage, LOGOUT_PENDING_KEY);
  const closure = safeRead(storage, CLOSURE_UNCONFIRMED_KEY);
  const validHint = hint.value === "1";
  const validPending = pending.value === "1";
  const validClosure = closure.value === "1";
  if (hint.value !== null && !validHint) safeRemove(storage, SIGNED_IN_HINT_KEY);
  if (pending.value !== null && !validPending) safeRemove(storage, LOGOUT_PENDING_KEY);
  if (closure.value !== null && !validClosure) safeRemove(storage, CLOSURE_UNCONFIRMED_KEY);

  let snapshot = {
    status: validPending ? "signout_pending" : token ? "unknown" : validHint ? "unknown" : "anonymous",
    user: null,
    landing: token && !validPending ? "consuming" : "idle",
    message: hint.error || pending.error || closure.error ? "Browser storage is unavailable." : null,
    notice: null,
    retryAfter: null,
    closureUnconfirmed: validClosure,
    closureMessage: validClosure ? CLOSURE_UNCONFIRMED_MESSAGE : null,
  };

  const emit = (patch) => {
    if (destroyed) return;
    snapshot = { ...snapshot, ...patch };
    listeners.forEach((listener) => listener());
  };

  const clearAllPrivateData = () => {
    for (const owner of privateOwners.values()) owner.clearPrivate();
  };

  const becomeAnonymous = (message = null, notice = null) => {
    generation += 1;
    profileController?.abort();
    clearAllPrivateData();
    safeRemove(storage, SIGNED_IN_HINT_KEY);
    emit({ status: "anonymous", user: null, landing: "idle", message, notice, retryAfter: null });
  };

  const handleUnauthorized = ({ path, method } = {}) => {
    // Account closure classifies 401 separately: the session is gone, but the
    // destructive outcome is unconfirmed. Let that in-flight owner persist the
    // closure marker and render the honest support path.
    if (closureFlight && path === "/users/me" && method === "DELETE") return;
    becomeAnonymous(null, "Your session expired. Sign in again.");
  };

  const activeAccountClosurePatch = () => {
    if (!snapshot.closureUnconfirmed) return {};
    safeRemove(storage, CLOSURE_UNCONFIRMED_KEY);
    return {
      closureUnconfirmed: false,
      closureMessage: null,
      notice: CLOSURE_ACTIVE_MESSAGE,
    };
  };

  const revalidate = ({ explicit = false } = {}) => {
    if (profileFlight) return profileFlight;
    const requestGeneration = generation;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    profileController = controller;
    if (explicit) emit({ status: "unknown", message: null, retryAfter: null });
    profileFlight = api.getProfile({ signal: controller?.signal })
      .then((user) => {
        if (requestGeneration !== generation) return snapshot;
        safeWrite(storage, SIGNED_IN_HINT_KEY, "1");
        emit({
          status: "signed_in",
          user: publicUser(user),
          message: null,
          retryAfter: null,
          ...activeAccountClosurePatch(),
        });
        return snapshot;
      })
      .catch((error) => {
        if (requestGeneration !== generation) return snapshot;
        if (error?.status === 401) {
          becomeAnonymous(null);
        } else {
          emit({
            status: "unknown",
            user: null,
            message: `We could not verify your session. Check your connection and retry.${error?.requestId ? ` Request ID: ${error.requestId}` : ""}`,
            retryAfter: error?.retryAfter || null,
          });
        }
        return snapshot;
      })
      .finally(() => {
        if (profileController === controller) profileController = null;
        profileFlight = null;
      });
    return profileFlight;
  };

  const consumeMagicLink = () => {
    if (!tokenInPageMemory || consumeFlight) return consumeFlight || Promise.resolve(snapshot);
    if (!storageAvailable(storage)) {
      emit({ status: "anonymous", landing: "storage_required", message: STORAGE_REQUIRED_MESSAGE });
      return Promise.resolve(snapshot);
    }
    const requestGeneration = ++generation;
    emit({ status: "unknown", landing: "consuming", message: null, retryAfter: null });
    consumeFlight = api.consumeMagicLink(tokenInPageMemory)
      .then((user) => {
        if (requestGeneration !== generation) return snapshot;
        tokenInPageMemory = null;
        const persisted = safeWrite(storage, SIGNED_IN_HINT_KEY, "1");
        emit({
          status: "signed_in",
          user: publicUser(user),
          landing: "success",
          message: persisted ? "You are signed in." : "Signed in, but browser storage could not remember this session.",
          retryAfter: null,
          ...activeAccountClosurePatch(),
        });
        return snapshot;
      })
      .catch((error) => {
        if (requestGeneration !== generation) return snapshot;
        tokenInPageMemory = null;
        safeRemove(storage, SIGNED_IN_HINT_KEY);
        emit({
          status: "anonymous",
          user: null,
          landing: "error",
          message: error?.status === 401
            ? "This sign-in link is invalid or expired. Request a new link."
            : "We could not complete sign-in. Request a new link and try again.",
          retryAfter: error?.retryAfter || null,
        });
        return snapshot;
      })
      .finally(() => { consumeFlight = null; });
    return consumeFlight;
  };

  const requestMagicLink = (email) => {
    if (requestFlight) return requestFlight;
    const currentRequestGeneration = ++requestGeneration;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    requestController = controller;
    emit({ message: null, retryAfter: null });
    const flight = api.requestMagicLink(String(email || "").trim(), { signal: controller?.signal })
      .then((result) => {
        if (currentRequestGeneration !== requestGeneration) return result;
        emit({ message: "Check your email for a sign-in link.", retryAfter: null });
        return result;
      })
      .catch((error) => {
        if (currentRequestGeneration !== requestGeneration || error?.name === "AbortError") throw error;
        emit({
          message: error?.status === 429
            ? `Too many requests. Try again in ${error.retryAfter || 60} seconds.`
            : `We could not send a sign-in link.${error?.requestId ? ` Request ID: ${error.requestId}` : ""}`,
          retryAfter: error?.retryAfter || null,
        });
        throw error;
      })
      .finally(() => {
        if (requestFlight === flight) requestFlight = null;
        if (requestController === controller) requestController = null;
      });
    requestFlight = flight;
    return flight;
  };

  const cancelMagicLinkRequest = () => {
    requestGeneration += 1;
    requestController?.abort();
    requestController = null;
    requestFlight = null;
  };

  const updateProfile = (displayName) => {
    if (profileUpdateFlight) return profileUpdateFlight;
    if (closureFlight) {
      return Promise.reject(accountMutationConflict("Account closure is already in progress."));
    }
    if (snapshot.status !== "signed_in" || !snapshot.user) {
      return Promise.reject(new Error("A signed-in profile is required."));
    }
    const requestGeneration = generation;
    const previousUser = snapshot.user;
    const optimisticName = String(displayName ?? "").trim() || null;
    emit({ user: { ...previousUser, display_name: optimisticName } });
    const flight = api.updateProfile(displayName)
      .then((user) => {
        if (requestGeneration === generation) emit({ user: publicUser(user) });
        return user;
      })
      .catch((error) => {
        if (requestGeneration === generation && error?.status !== 401) {
          emit({ user: previousUser });
        }
        throw error;
      })
      .finally(() => {
        if (profileUpdateFlight === flight) profileUpdateFlight = null;
      });
    profileUpdateFlight = flight;
    return flight;
  };

  const recordUnconfirmedClosure = ({ pendingRevocation, message }) => {
    generation += 1;
    profileController?.abort();
    const closureMarkerWritten = safeWrite(storage, CLOSURE_UNCONFIRMED_KEY, "1");
    safeRemove(storage, SIGNED_IN_HINT_KEY);
    const pendingMarkerWritten = pendingRevocation
      ? safeWrite(storage, LOGOUT_PENDING_KEY, "1")
      : safeRemove(storage, LOGOUT_PENDING_KEY);
    const storageWarning = closureMarkerWritten && pendingMarkerWritten
      ? ""
      : " Browser storage could not record durable account-closure recovery.";
    emit({
      status: pendingRevocation ? "signout_pending" : "anonymous",
      user: null,
      landing: "idle",
      message: pendingRevocation ? `${message}${storageWarning}` : null,
      notice: null,
      retryAfter: null,
      closureUnconfirmed: true,
      closureMessage: `${CLOSURE_UNCONFIRMED_MESSAGE}${storageWarning}`,
    });
  };

  const closeAccount = () => {
    if (closureFlight) return closureFlight;
    if (profileUpdateFlight) {
      return Promise.reject(accountMutationConflict("Finish saving the profile before closing the account."));
    }
    // Closure may take effect even when its response is lost. Invalidate every
    // private owner and all older generation-bound work before dispatch.
    generation += 1;
    profileController?.abort();
    clearAllPrivateData();
    const flight = api.deleteAccount()
      .then(() => {
        generation += 1;
        profileController?.abort();
        safeRemove(storage, SIGNED_IN_HINT_KEY);
        safeRemove(storage, LOGOUT_PENDING_KEY);
        safeRemove(storage, CLOSURE_UNCONFIRMED_KEY);
        emit({
          status: "anonymous",
          user: null,
          landing: "idle",
          message: null,
          notice: null,
          retryAfter: null,
          closureUnconfirmed: false,
          closureMessage: null,
        });
        return snapshot;
      })
      .catch((error) => {
        if (error?.status === 403 || error?.status === 429) throw error;
        if (error?.status === 401) {
          recordUnconfirmedClosure({ pendingRevocation: false });
        } else {
          const requestReference = error?.requestId ? ` Reference: ${error.requestId}.` : "";
          recordUnconfirmedClosure({
            pendingRevocation: true,
            message: `Account closure could not be confirmed. Finish signing out, then contact support before trying again.${requestReference}`,
          });
        }
        return snapshot;
      })
      .finally(() => {
        if (closureFlight === flight) closureFlight = null;
      });
    closureFlight = flight;
    return flight;
  };

  const finishLogout = () => {
    generation += 1;
    onlineRetryArmed = false;
    safeRemove(storage, SIGNED_IN_HINT_KEY);
    const markerRemoved = safeRemove(storage, LOGOUT_PENDING_KEY);
    emit({
      status: "anonymous",
      user: null,
      landing: "idle",
      message: markerRemoved ? null : "Signed out, but browser storage cleanup needs attention.",
      notice: null,
      retryAfter: null,
    });
  };

  const continueAfterLogout = async () => {
    finishLogout();
    if (tokenInPageMemory) return consumeMagicLink();
    return snapshot;
  };

  const logout = ({ automatic = false } = {}) => {
    if (logoutFlight) return logoutFlight;
    generation += 1;
    profileController?.abort();
    clearAllPrivateData();
    const markerWritten = safeWrite(storage, LOGOUT_PENDING_KEY, "1");
    safeRemove(storage, SIGNED_IN_HINT_KEY);
    emit({
      status: "signout_pending",
      user: null,
      landing: "idle",
      message: markerWritten ? null : "Browser storage could not preserve the pending sign-out.",
      retryAfter: null,
    });
    logoutFlight = api.logout()
      .then(() => continueAfterLogout())
      .catch(async (error) => {
        if (error?.status === 401) {
          return continueAfterLogout();
        } else {
          onlineRetryArmed = true;
          emit({
            status: "signout_pending",
            message: `${automatic ? "Automatic sign-out" : "Sign-out"} did not finish. Retry before continuing.${markerWritten ? "" : " Browser storage could not record durable recovery."}`,
            retryAfter: error?.retryAfter || null,
          });
        }
        return snapshot;
      })
      .finally(() => { logoutFlight = null; });
    return logoutFlight;
  };

  const start = () => {
    if (started) return Promise.resolve(snapshot);
    started = true;
    if (validPending) return logout({ automatic: true });
    if (tokenInPageMemory) return consumeMagicLink();
    if (validHint) return revalidate();
    return Promise.resolve(snapshot);
  };

  const onStorage = (event) => {
    if (event.storageArea && event.storageArea !== storage) return;
    if (event.key === SIGNED_IN_HINT_KEY) {
      if (event.newValue === null) becomeAnonymous(null);
      else if (event.newValue === "1") void revalidate({ explicit: true });
    }
    if (event.key === LOGOUT_PENDING_KEY && event.newValue === "1") {
      generation += 1;
      profileController?.abort();
      clearAllPrivateData();
      emit({ status: "signout_pending", user: null, message: "Sign-out is pending in another tab." });
    } else if (event.key === LOGOUT_PENDING_KEY && event.newValue === null && snapshot.status === "signout_pending") {
      becomeAnonymous(null);
    }
    if (event.key === CLOSURE_UNCONFIRMED_KEY) {
      const isUnconfirmed = event.newValue === "1";
      if (event.newValue !== null && !isUnconfirmed) {
        safeRemove(storage, CLOSURE_UNCONFIRMED_KEY);
      }
      emit({
        closureUnconfirmed: isUnconfirmed,
        closureMessage: isUnconfirmed ? CLOSURE_UNCONFIRMED_MESSAGE : null,
      });
    }
  };
  const onOnline = () => {
    if (!onlineRetryArmed || snapshot.status !== "signout_pending") return;
    onlineRetryArmed = false;
    void logout({ automatic: true });
  };
  windowLike?.addEventListener?.("storage", onStorage);
  windowLike?.addEventListener?.("online", onOnline);

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    start,
    revalidate,
    retryConsume: consumeMagicLink,
    requestMagicLink,
    cancelMagicLinkRequest,
    updateProfile,
    closeAccount,
    logout,
    handleUnauthorized,
    registerPrivateOwner(key, owner) {
      if (!key || privateOwners.has(key)) throw new Error(`Duplicate private-data owner: ${key}`);
      if (
        !owner || typeof owner.load !== "function" || typeof owner.invalidate !== "function" ||
        typeof owner.clearPrivate !== "function"
      ) {
        throw new Error(`Private-data owner ${key} must provide load(), invalidate(), and clearPrivate()`);
      }
      privateOwners.set(key, owner);
      return () => privateOwners.delete(key);
    },
    clearAllPrivateData,
    dismissLanding() {
      emit({ landing: "idle", message: null, retryAfter: null });
    },
    dismissNotice() {
      emit({ notice: null });
    },
    dismissClosureNotice() {
      safeRemove(storage, CLOSURE_UNCONFIRMED_KEY);
      emit({ closureUnconfirmed: false, closureMessage: null });
    },
    destroy() {
      destroyed = true;
      generation += 1;
      tokenInPageMemory = null;
      profileController?.abort();
      cancelMagicLinkRequest();
      listeners.clear();
      windowLike?.removeEventListener?.("storage", onStorage);
      windowLike?.removeEventListener?.("online", onOnline);
    },
  };
}

const browserToken = takeCapturedMagicLinkToken();
export const sessionStore = createSessionStore({ token: browserToken });
setAccountUnauthorizedHandler((context) => sessionStore.handleUnauthorized(context));

export function useSession(store = sessionStore) {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export { STORAGE_REQUIRED_MESSAGE };
